import { useState, useEffect, useRef } from 'react';

const STORAGE_KEY = 'tpp_boot_complete';

function getInitialPhase(): number {
  if (typeof window === 'undefined') return 0;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 5;
  try { if (sessionStorage.getItem(STORAGE_KEY)) return 5; } catch {}
  return 0;
}

/**
 * Cinematic boot sequence that plays once per browser session.
 * Returns { phase, isComplete, overlayText } where phase 0-5 drives conditional rendering.
 *
 * Phase 0: mount, content hidden
 * Phase 1: "INITIALIZING COMMAND CENTER..."
 * Phase 2: "LOADING OPERATIVE PROFILE..." + ticker fades in
 * Phase 3: greeting entrance-animates, overlay clears
 * Phase 4: stat cards entrance (waits for dataReady)
 * Phase 5: remaining zones stagger in — sequence complete
 *
 * Respects prefers-reduced-motion: skips to phase 5 immediately.
 * Guards against React 18 Strict Mode double-mount via useRef.
 */
export function useBootSequence(dataReady: boolean): {
  phase: number;
  isComplete: boolean;
  overlayText: string;
} {
  const [phase, setPhase] = useState(getInitialPhase);
  const bootStarted = useRef(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const shouldSkip = useRef(phase >= 5);

  // Run the timed sequence
  useEffect(() => {
    if (shouldSkip.current || bootStarted.current) return;
    bootStarted.current = true;

    // Mark as complete immediately to guard against double-mount
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch {}

    // Phase progression on fixed timeline
    timers.current.push(setTimeout(() => setPhase(1), 200));
    timers.current.push(setTimeout(() => setPhase(2), 600));
    timers.current.push(setTimeout(() => setPhase(3), 1000));

    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, []);

  // Phase 4 → 5: wait for data before stat card reveal
  useEffect(() => {
    if (shouldSkip.current) return;
    if (phase === 3 && dataReady) {
      const t = setTimeout(() => setPhase(4), 200);
      timers.current.push(t);
      return () => clearTimeout(t);
    }
    if (phase === 4) {
      const t = setTimeout(() => setPhase(5), 400);
      timers.current.push(t);
      return () => clearTimeout(t);
    }
  }, [phase, dataReady]);

  const isComplete = phase >= 5;

  const overlayText =
    phase === 1 ? '> INITIALIZING COMMAND CENTER...' :
    phase === 2 ? '> LOADING OPERATIVE PROFILE...' :
    '';

  return { phase, isComplete, overlayText };
}
